import React, { useEffect, useState } from 'react';
import { ExternalLink, FileSpreadsheet, Loader2, Plus, X } from 'lucide-react';
import { errorText } from '../ErrorBanner';
import { amount } from '../../utils/format';
import { QuoteInput, quoteApi } from '../../services/quoteApi';

const cell = 'w-full px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500';

/**
 * 견적서 만들기(작업 6, 2026-10-22) — 재무계획의 사람 · MM 을 직급별로 가져오고 **청구 단가**는 여기서 넣는다.
 * 재무계획의 단가는 내부 인력원가라 견적서에 싣지 않는다. 만들면 드라이브 「06.견적」 에 엑셀로 올라간다.
 */
export const QuotePanel: React.FC<{ projectId: number }> = ({ projectId }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState<QuoteInput | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ fileName: string; url: string } | null>(null);

  useEffect(() => {
    if (!open || q) return;
    quoteApi.quoteDraft(projectId).then(setQ).catch((e) => setError(errorText(e, '견적 기본값을 불러오지 못했습니다')));
  }, [open, q, projectId]);

  const labor = (q?.rows ?? []).reduce((a, r) => a + r.people * r.months * r.rate * r.utilization, 0);
  const total = labor + Math.round(labor * (q?.expenseRate ?? 0)) + (q?.research ?? 0);
  const setRow = (i: number, patch: Partial<QuoteInput['rows'][number]>) => q && setQ({ ...q, rows: q.rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) });

  const make = async () => {
    if (!q) return;
    setBusy(true); setError(null);
    try { setDone(await quoteApi.quote(projectId, q)); }
    catch (e) { setError(errorText(e, '견적서를 만들지 못했습니다')); }
    finally { setBusy(false); }
  };

  return (
    <section className="mt-4 bg-white rounded-lg border border-slate-200">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
        <h2 className="text-sm font-semibold text-slate-900">견적서</h2>
        <span className="text-xs text-slate-400">양식(03_양식/견적서양식_컨설팅)을 채워 드라이브 「06.견적」 에 올립니다</span>
        <button onClick={() => setOpen(!open)} className="ml-auto px-2.5 py-1 rounded-lg text-xs font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 flex items-center gap-1">
          <FileSpreadsheet size={12} /> {open ? '닫기' : '견적서 만들기'}
        </button>
      </div>
      {open && (
        <div className="px-4 py-3 space-y-3">
          {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}
          {!q ? <p className="text-xs text-slate-400 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> 불러오는 중...</p> : (
            <>
              <div className="grid gap-2 sm:grid-cols-[140px_1fr_2fr]">
                <label className="text-xs text-slate-500">견적일<input type="date" value={q.date} onChange={(e) => setQ({ ...q, date: e.target.value })} className={`mt-1 ${cell}`} /></label>
                <label className="text-xs text-slate-500">받는 곳 (○○ 귀중)<input value={q.client} onChange={(e) => setQ({ ...q, client: e.target.value })} className={`mt-1 ${cell}`} /></label>
                <label className="text-xs text-slate-500">견적명<input value={q.title} onChange={(e) => setQ({ ...q, title: e.target.value })} className={`mt-1 ${cell}`} /></label>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-xs text-slate-400 border-b border-slate-100">
                    <th className="text-left font-medium py-1.5 pr-2">구분</th><th className="text-left font-medium pr-2 w-28">Level</th>
                    <th className="text-right font-medium pr-2 w-16">인원</th><th className="text-right font-medium pr-2 w-20">기간(월)</th>
                    <th className="text-right font-medium pr-2 w-36">청구 단가(원/MM)</th><th className="text-right font-medium pr-2 w-20">투입율</th>
                    <th className="text-right font-medium pr-2 w-32">견적(원)</th><th className="w-8" />
                  </tr></thead>
                  <tbody>
                    {q.rows.map((r, i) => (
                      <tr key={i} className="border-b border-slate-50">
                        <td className="py-1 pr-2"><input value={r.role} onChange={(e) => setRow(i, { role: e.target.value })} className={cell} /></td>
                        <td className="pr-2"><input value={r.level} onChange={(e) => setRow(i, { level: e.target.value })} className={cell} /></td>
                        <td className="pr-2"><input type="number" min={0} value={r.people} onChange={(e) => setRow(i, { people: Number(e.target.value) })} className={`${cell} text-right`} /></td>
                        <td className="pr-2"><input type="number" min={0} step={0.1} value={r.months} onChange={(e) => setRow(i, { months: Number(e.target.value) })} className={`${cell} text-right`} /></td>
                        <td className="pr-2"><input type="number" min={0} step={1000000} value={r.rate} onChange={(e) => setRow(i, { rate: Number(e.target.value) })} className={`${cell} text-right ${r.rate ? '' : 'border-amber-300 bg-amber-50/40'}`} /></td>
                        <td className="pr-2"><input type="number" min={0} max={1} step={0.05} value={r.utilization} onChange={(e) => setRow(i, { utilization: Number(e.target.value) })} className={`${cell} text-right`} /></td>
                        <td className="pr-2 text-right tabular-nums">{amount(r.people * r.months * r.rate * r.utilization)}</td>
                        <td><button onClick={() => setQ({ ...q, rows: q.rows.filter((_, j) => j !== i) })} title="빼기" className="p-1 rounded text-slate-400 hover:bg-slate-100"><X size={14} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {q.rows.length < 4 && (
                  <button onClick={() => setQ({ ...q, rows: [...q.rows, { role: '', level: '', people: 1, months: 1, rate: 0, utilization: 1 }] })}
                    className="mt-1 text-xs text-indigo-600 hover:underline flex items-center gap-0.5"><Plus size={12} /> 줄 추가 (4줄까지 — 양식 칸)</button>
                )}
              </div>
              <div className="grid gap-2 sm:grid-cols-4 items-end">
                <label className="text-xs text-slate-500">부대경비 비율(%)<input type="number" min={0} max={100} step={0.5} value={Math.round(q.expenseRate * 1000) / 10} onChange={(e) => setQ({ ...q, expenseRate: Number(e.target.value) / 100 })} className={`mt-1 ${cell} text-right`} /></label>
                <label className="text-xs text-slate-500">리서치 비용(원)<input type="number" min={0} value={q.research} onChange={(e) => setQ({ ...q, research: Number(e.target.value) })} className={`mt-1 ${cell} text-right`} /></label>
                <label className="text-xs text-slate-500">견적금액(원, VAT 포함)<input type="number" min={0} value={q.amount} onChange={(e) => setQ({ ...q, amount: Number(e.target.value) })} className={`mt-1 ${cell} text-right`} /></label>
                <p className="text-xs text-slate-500">산출 총계 <b className="text-slate-800">{amount(total)}</b><br />Write-off <b className="text-slate-800">{total > 0 ? `${Math.max(0, (1 - q.amount / (total * 1.1)) * 100).toFixed(1)}%` : '—'}</b> <span className="text-slate-400">(총계+VAT 와 견줌)</span></p>
              </div>
              <p className="text-xs text-slate-400">사람 · 기간은 재무계획에서 가져왔습니다. 재무계획의 단가는 내부 원가라 쓰지 않고, 청구 단가는 기본값(파트너 5천만 · 컨설턴트 3천만/MM)에서 고칩니다. 견적금액은 VAT 포함이고 기본값은 제안가입니다.</p>
              <div className="flex items-center justify-end gap-2">
                {done && <a href={done.url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline flex items-center gap-0.5 mr-auto">{done.fileName} <ExternalLink size={11} /></a>}
                <button onClick={make} disabled={busy || !q.rows.length || q.amount <= 0}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 flex items-center gap-1.5">
                  {busy && <Loader2 size={14} className="animate-spin" />} 만들어 드라이브에 올리기
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
};
